import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Banner, Button, DataTable, DefinitionList, Drawer, EmptyState, FilterBar, InfoButton, Inline, PageHeader, Stack, t, Text, toast,
} from '@kanbios/ui';
import type { audit } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { RefusalPage } from '../../components/Refusal';
import { api, ApiFailure, unwrap } from '../../lib/api';
import { clockTime, relativeTime, shortDateTime } from '../../lib/format';
import { useFillHeight } from '../../lib/useFillHeight';
import { useSession } from '../../lib/session';

type Event = audit.components['schemas']['AuditEvent'];
const PERIODS = ['today', '7d', '30d', '12m'] as const;
const DAYS = { today: 1, '7d': 7, '30d': 30, '12m': 365 } as const;
const RESULT_TONES = { success: 'pos', denied: 'neg', failure: 'neg' } as const;
const SIEM_TONES = { operational: 'pos', late: 'warn', failing: 'neg', not_configured: 'neutral' } as const;

/**
 * « Journal d'audit » (SPEC-012), DSM Index page type: every action on the
 * platform, filtered and paged on the server, the chain's integrity, an
 * entry's detail, the export (itself traced) and the state of the SIEM flow.
 */
export function AuditPage() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const [fill, fillHeight] = useFillHeight();
  const [filters, setFilters] = useState<Record<string, unknown>>({ period: 'today' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [siemOpen, setSiemOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tooLarge, setTooLarge] = useState<string | null>(null);

  const period = (filters.period as (typeof PERIODS)[number] | undefined) ?? 'today';
  const range = filters.range as [string | Date | null, string | Date | null] | undefined;
  // Computed when the period changes, not at each render: the query key must stay stable.
  const { from, to } = useMemo(() => ({
    from: range?.[0] ? new Date(range[0]).toISOString() : new Date(Date.now() - DAYS[period] * 86_400_000).toISOString(),
    to: range?.[1] ? new Date(new Date(range[1]).getTime() + 86_399_999).toISOString() : undefined,
  }), [period, range]);
  const asList = (v: unknown) => (Array.isArray(v) ? v.join(',') : typeof v === 'string' ? v : undefined);
  const filter = {
    category: asList(filters.category), result: asList(filters.result), origin: asList(filters.origin),
    actor: typeof filters.actor === 'string' ? filters.actor : undefined, target: typeof filters.target === 'string' ? filters.target : undefined,
  };
  const query = Object.fromEntries(Object.entries(filter).filter((e): e is [string, string] => Boolean(e[1])));
  const list = useQuery({
    queryKey: ['audit-events', from, to, query, page, pageSize],
    queryFn: async () => unwrap(await api.audit.GET('/events', { params: { query: { page, pageSize, from, to, filter: query } } })),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    retry: false,
  });
  const integrity = useQuery({ queryKey: ['audit-integrity'], queryFn: async () => unwrap(await api.audit.GET('/integrity')), refetchInterval: 60_000, retry: false });
  const siem = useQuery({ queryKey: ['audit-siem'], queryFn: async () => unwrap(await api.audit.GET('/siem')), enabled: siemOpen, refetchInterval: siemOpen ? 15_000 : false });
  const selected = params.get('entree');
  const detail = useQuery({
    queryKey: ['audit-event', selected],
    queryFn: async () => unwrap(await api.audit.GET('/events/{event}', { params: { path: { event: Number(selected) } } })),
    enabled: Boolean(selected),
    retry: false,
  });

  if (list.error && !list.data) return <Shell crumbs={[{ label: t('nav.reports') }, { label: t('audit.title') }]}><RefusalPage error={list.error} /></Shell>;

  const exportCsv = async () => {
    setExporting(true);
    setTooLarge(null);
    try {
      const job = unwrap(await api.audit.POST('/exports', { body: { from, to, ...query } }));
      const ready = unwrap(await api.audit.GET('/exports/{job}', { params: { path: { job: job.jobId } } }));
      window.location.assign(ready.url);
      toast({ tone: 'pos', title: t('audit.exported', { n: ready.rows }) });
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'VALIDATION_FAILED' && e.body.details?.count) setTooLarge(e.body.message);
      else if (e instanceof ApiFailure) toast({ tone: 'neg', title: t('audit.exportFailed', { requestId: e.body.requestId }) });
    } finally {
      setExporting(false);
    }
  };

  const facets = list.data?.facets;
  const options = (key: 'category' | 'result' | 'origin') => Object.entries(facets?.[key] ?? {})
    .map(([value, n]) => ({ value, label: `${t(`audit.${key}.${value}`, {}) === `audit.${key}.${value}` ? value : t(`audit.${key}.${value}`)} (${n})` }));
  const filterDefs = [
    { key: 'period', label: t('audit.filters.period'), type: 'select' as const, pinned: true, options: PERIODS.map(value => ({ value, label: t(`audit.periods.${value}`) })) },
    { key: 'range', label: t('audit.filters.range'), type: 'daterange' as const },
    { key: 'actor', label: t('audit.filters.actor'), type: 'text' as const },
    { key: 'category', label: t('audit.filters.category'), type: 'multi' as const, options: options('category') },
    { key: 'target', label: t('audit.filters.target'), type: 'text' as const },
    { key: 'result', label: t('audit.filters.result'), type: 'multi' as const, options: options('result') },
    { key: 'origin', label: t('audit.filters.origin'), type: 'multi' as const, options: options('origin') },
  ];
  const actionLabel = (code: string) => { const label = t(`audit.actions.${code}`); return label === `audit.actions.${code}` ? code : label; };
  // One line per entry: who, what, on what, with which result; the secondary facts are in the « Colonnes » menu and the entry's detail.
  const originLabel = (origin: string) => (t(`audit.origin.${origin}`) === `audit.origin.${origin}` ? origin : t(`audit.origin.${origin}`));
  const columns = [
    { key: 'time', label: t('audit.columns.time'), width: 170, mono: true, pinned: true, render: (e: Event) => shortDateTime(e.time) },
    // SPEC-012: the actor's name and its nature (human or technical), on one line.
    { key: 'actor', label: t('audit.columns.actor'), width: 260, accessor: (e: Event) => e.actor.name, render: (e: Event) => (
      <Inline gap={8} align="center" wrap={false}><Text variant="body-sm" truncate>{e.actor.name}</Text><Text variant="caption" tone="mute">{t(`audit.actorKind.${e.actor.kind}`)}</Text></Inline>
    ) },
    { key: 'category', label: t('audit.columns.category'), width: 150, render: (e: Event) => <Badge tone="neutral">{t(`audit.category.${e.category}`)}</Badge> },
    { key: 'action', label: t('audit.columns.action'), width: 240, grow: true, render: (e: Event) => actionLabel(e.action) },
    { key: 'target', label: t('audit.columns.target'), width: 200, mono: true, render: (e: Event) => e.target ?? '—' },
    { key: 'result', label: t('audit.columns.result'), width: 110, render: (e: Event) => <Badge tone={RESULT_TONES[e.result]}>{t(`audit.result.${e.result}`)}</Badge> },
    { key: 'origin', label: t('audit.columns.origin'), width: 130, render: (e: Event) => originLabel(e.origin) },
  ];
  const total = list.data?.total ?? 0;
  const chain = integrity.data;
  const filtered = Object.keys(query).length > 0 || Boolean(range);

  return (
    <Shell crumbs={[{ label: t('nav.reports') }, { label: t('audit.title') }]}>
      <Stack gap={16}>
        <PageHeader
          title={t('audit.title')}
          subtitle={t('audit.subtitle')}
          actions={(
            <Inline gap={8}>
              <Button variant="secondary" icon="podcasts" onClick={() => setSiemOpen(true)}>{t('audit.siem.open')}</Button>
              {can('audit.export') && <Button icon="download" disabled={exporting || total === 0} onClick={() => void exportCsv()}>{exporting ? t('audit.exporting') : t('audit.export')}</Button>}
            </Inline>
          )}
        />
        {chain?.status === 'broken' && <Banner tone="neg" title={t('audit.integrity.broken', { n: (chain.brokenAt ?? 0).toLocaleString('fr-FR') })} />}
        {chain?.status === 'late' && <Alert tone="warn" title={t('audit.integrity.late', { when: chain.lastCheckAt ? relativeTime(chain.lastCheckAt) : '—' })} />}
        {chain && (
          <Inline gap={8} align="center" wrap>
            <Badge tone={chain.status === 'verified' ? 'pos' : chain.status === 'late' ? 'warn' : 'neg'}>
              {chain.status === 'verified' ? t('audit.integrity.verified', { when: chain.lastCheckAt ? relativeTime(chain.lastCheckAt) : '—' }) : t(`audit.integrity.badge.${chain.status}`)}
            </Badge>
            <Text variant="body-sm">{t(chain.lockedCopy?.configured ? 'audit.integrity.line' : 'audit.integrity.lineNoCopy')}</Text>
            <InfoButton label={t('audit.integrity.how')} title={t('audit.integrity.howTitle')} size="sm"><Text variant="body-sm">{t('audit.integrity.howText')}</Text></InfoButton>
          </Inline>
        )}
        {tooLarge && <Alert tone="warn" title={tooLarge} />}
        <div ref={fill}>
          <DataTable
            height={fillHeight}
            label={t('audit.table')}
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey="sequence"
            manual
            totalCount={total}
            toolbarStart={<FilterBar label={t('audit.filters.label')} filters={filterDefs} value={filters} onChange={v => { setFilters({ period: 'today', ...(v as Record<string, unknown>) }); setPage(1); }} />}
            toolbarSearch={false}
            toolbarFilters={false}
            density="compact"
            loading={list.isPending}
            onRowClick={(e: Event) => setParams({ entree: String(e.sequence) })}
            activeRowKey={selected ? Number(selected) : undefined}
            empty={filtered
              ? <EmptyState icon="filter_alt_off" title={t('audit.empty.filtered')} />
              : <EmptyState icon="history" title={t('audit.empty.period')} action={<Button variant="secondary" onClick={() => setFilters({ period: '30d' })}>{t('audit.empty.widen')}</Button>} />}
            pagination={{ page, pageSize, total, onPageChange: setPage, pageSizes: [50, 100], onPageSizeChange: size => { setPageSize(size); setPage(1); } }}
          />
        </div>
      </Stack>

      <Drawer open={Boolean(selected)} onClose={() => setParams({})} size="xl" title={t('audit.detail.title', { n: selected ?? '' })}>
        {detail.error ? <RefusalPage error={detail.error} /> : detail.data && (
          <DefinitionList items={[
            { label: t('audit.columns.time'), value: `${shortDateTime(detail.data.time)} (${detail.data.time})` },
            { label: t('audit.columns.actor'), value: `${detail.data.actor.name} · ${detail.data.actor.ref}` },
            { label: t('audit.detail.kind'), value: t(`audit.actorKind.${detail.data.actor.kind}`) },
            { label: t('audit.columns.category'), value: t(`audit.category.${detail.data.category}`) },
            { label: t('audit.columns.action'), value: `${actionLabel(detail.data.action)} (${detail.data.action})` },
            { label: t('audit.columns.target'), value: detail.data.target ?? '—' },
            { label: t('audit.columns.result'), value: t(`audit.result.${detail.data.result}`) },
            { label: t('audit.columns.origin'), value: detail.data.origin },
            { label: t('audit.detail.request'), value: <Text variant="code">{detail.data.requestId}</Text> },
            ...(detail.data.details ? [{ label: t('audit.detail.details'), value: <Text variant="code">{JSON.stringify(detail.data.details)}</Text> }] : []),
            { label: t('audit.detail.previousHash'), value: <Text variant="code">{detail.data.previousHash}</Text> },
            { label: t('audit.detail.hash'), value: <Text variant="code">{detail.data.hash}</Text> },
          ]} />
        )}
      </Drawer>

      <Drawer open={siemOpen} onClose={() => setSiemOpen(false)} size="xl" title={t('audit.siem.title')} subtitle={t('audit.siem.subtitle')}>
        {siem.data && (
          <Stack gap={12}>
            <Inline gap={8} align="center">
              <Badge tone={SIEM_TONES[siem.data.state]}>{siem.data.state === 'late' ? t('audit.siem.late', { n: siem.data.lagMinutes }) : t(`audit.siem.state.${siem.data.state}`)}</Badge>
            </Inline>
            <DefinitionList items={[
              { label: t('audit.siem.destination'), value: siem.data.destination ?? t('audit.siem.none') },
              { label: t('audit.siem.format'), value: siem.data.format },
              { label: t('audit.siem.lastSent'), value: siem.data.lastSentAt ? `${clockTime(siem.data.lastSentAt)} · ${relativeTime(siem.data.lastSentAt)}` : '—' },
              { label: t('audit.siem.behind'), value: t('audit.siem.behindValue', { n: siem.data.behind, minutes: siem.data.lagMinutes }) },
              ...(siem.data.lastError ? [{ label: t('audit.siem.lastError'), value: siem.data.lastError }] : []),
            ]} />
            <Text variant="body-sm" tone="mute">{t('audit.siem.change')}</Text>
          </Stack>
        )}
      </Drawer>
    </Shell>
  );
}
